/**
 * PersonaIndicatorCompact Component
 * 
 * Compact version of PersonaIndicator for Story Pane
 * Shows active AI persona in a horizontal layout with readiness badges
 * 
 * Features:
 * - Horizontal compact layout
 * - Real-time persona display
 * - Intervention count tracking
 * - Inline readiness badges for Quiz & Role personas
 * - Visual persona switching animation
 * - Color-coded persona types
 */

import React, { useEffect, useState, useCallback } from 'react';
import PersonaReadinessBadgeCompact from './PersonaReadinessBadgeCompact';
import { PersonaReadiness } from '../services/storyAnalysisService';
import { getApiUrl } from '../utils/apiUtils';

interface PersonaIndicatorCompactProps {
  conversationId: string;
  storyId?: string | null;
  quizReadiness?: PersonaReadiness | null;
  roleReadiness?: PersonaReadiness | null;
  isAnalyzing?: boolean;
  voiceLiveClient?: any; // VoiceLive client for WebSocket events
  quizDialogEnabled?: boolean; // Quiz dialog enable/disable preference
  onQuizDialogEnabledChange?: (enabled: boolean) => void; // Callback to update preference
  roleRangerEnabled?: boolean; // Role Ranger enable/disable preference  
  onRoleRangerEnabledChange?: (enabled: boolean) => void; // Callback to update preference
  pronunciationEnabled?: boolean; // Pronunciation feedback enable/disable
  onPronunciationEnabledChange?: (enabled: boolean) => void; // Callback to update preference
  pronunciationActive?: boolean; // Is pronunciation currently recording
  onActivePersonaChange?: (persona: string | null) => void; // Callback when active persona changes
}

interface PersonaStatus {
  conversation_id: string;
  active_persona: string | null;
  intervention_count: number;
  max_interventions: number;
  can_intervene: boolean;
  remaining_interventions: number;
  persona_switch_count: number;
}

const PersonaIndicatorCompact: React.FC<PersonaIndicatorCompactProps> = ({ 
  conversationId,
  storyId = null,
  quizReadiness = null,
  roleReadiness = null,
  isAnalyzing = false,
  voiceLiveClient,
  quizDialogEnabled = false,
  onQuizDialogEnabledChange,
  roleRangerEnabled = true,
  onRoleRangerEnabledChange,
  pronunciationEnabled = false,
  onPronunciationEnabledChange,
  pronunciationActive = false,
  onActivePersonaChange
}) => {
  const [personaStatus, setPersonaStatus] = useState<PersonaStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previousPersona, setPreviousPersona] = useState<string | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isForceReturning, setIsForceReturning] = useState(false);
  const [personaActiveSeconds, setPersonaActiveSeconds] = useState<number>(0);
  const [showStuckWarning, setShowStuckWarning] = useState(false);

  // Track persona active duration for stuck state detection
  useEffect(() => {
    if (!personaStatus || personaStatus.active_persona === 'gentle_teacher') {
      setPersonaActiveSeconds(0);
      setShowStuckWarning(false);
      return;
    }

    // Start timer when intervention persona is active
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setPersonaActiveSeconds(elapsed);
      
      // Show warning after 60 seconds
      if (elapsed >= 60) {
        setShowStuckWarning(true);
      }
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personaStatus?.active_persona]);

  // Force return to Gentle Teacher (emergency escape button)
  const handleForceReturn = async () => {
    if (!conversationId || isForceReturning) return;
    
    try {
      setIsForceReturning(true);
      console.log(`[PersonaIndicator] 🚨 Force-return triggered for conversation: ${conversationId}`);
      
      const response = await fetch(
        getApiUrl(`/voicelive/personas/force-return/${conversationId}`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        console.log(`[PersonaIndicator] ✅ Force-return successful:`, data);
        
        // Update UI immediately
        setPersonaStatus(prev => prev ? {
          ...prev,
          active_persona: 'gentle_teacher',
        } : null);
        
        // Trigger transition animation
        setIsTransitioning(true);
        setTimeout(() => setIsTransitioning(false), 600);
        
        // Fetch updated status to sync with server
        setTimeout(() => fetchPersonaStatus(), 500);
      } else {
        const errorData = await response.json();
        console.error('[PersonaIndicator] ❌ Force-return failed:', errorData);
        alert(`Failed to return to Gentle Teacher: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('[PersonaIndicator] ❌ Force-return error:', err);
      alert('Failed to return to Gentle Teacher. Please try again.');
    } finally {
      setIsForceReturning(false);
    }
  };

  // Fetch current persona status
  const fetchPersonaStatus = useCallback(async () => {
    try {
      console.log(`[PersonaIndicator] 📊 Fetching persona status for conversation: ${conversationId}`);
      const response = await fetch(
        getApiUrl(`/voicelive/personas/current?conversation_id=${conversationId}`)
      );
      
      if (response.ok) {
        const data: PersonaStatus = await response.json();
        console.log(`[PersonaIndicator] ✅ Persona status received:`, {
          active_persona: data.active_persona,
          intervention_count: data.intervention_count,
          max_interventions: data.max_interventions,
          can_intervene: data.can_intervene
        });
        
        // Detect persona change
        if (previousPersona && data.active_persona !== previousPersona) {
          console.log(`[PersonaIndicator] 🔄 Persona changed: ${previousPersona} → ${data.active_persona}`);
          setIsTransitioning(true);
          setTimeout(() => setIsTransitioning(false), 600);
        }
        
        setPreviousPersona(data.active_persona);
        setPersonaStatus(data);
        setError(null);
        
        // Notify parent component of active persona change
        if (onActivePersonaChange) {
          onActivePersonaChange(data.active_persona);
        }
      } else {
        console.warn(`[PersonaIndicator] ⚠️ Failed to fetch persona status: ${response.status}`);
        setError('Session not found');
      }
    } catch (err) {
      console.error('[PersonaIndicator] ❌ Persona status fetch error:', err);
      setError('Failed to fetch persona status');
    } finally {
      setLoading(false);
    }
  }, [conversationId, previousPersona, onActivePersonaChange]);

  // Listen for persona updates via WebSocket events
  useEffect(() => {
    if (!conversationId) return;

    console.log(`[PersonaIndicator] 🔌 Initializing for conversation: ${conversationId}`);
    
    // Initial fetch ONLY once when component mounts
    fetchPersonaStatus();

    // If voiceLiveClient is available, listen for real-time updates
    if (voiceLiveClient) {
      console.log('[PersonaIndicator] 📡 Setting up WebSocket event listeners');
      
      // Handle persona_switched events
      const handlePersonaSwitch = (event: any) => {
        console.log('[PersonaIndicator] 🎭 Persona switched event received:', {
          type: event.type,
          persona: event.persona,
          reason: event.reason,
          milestone: event.milestone,
          intervention_count: event.intervention_count,
          conversation_id: event.conversation_id
        });
        
        if (event.conversation_id === conversationId) {
          console.log(`[PersonaIndicator] ✅ Event matches current conversation, updating UI`);
          // Update persona status from WebSocket event
          const newStatus = {
            conversation_id: event.conversation_id,
            active_persona: event.persona,
            intervention_count: event.intervention_count || 0,
            max_interventions: 10, // Default
            can_intervene: true,
            remaining_interventions: 10 - (event.intervention_count || 0),
            persona_switch_count: 0
          };
          setPersonaStatus(newStatus);
          
          // Notify parent component of active persona change
          if (onActivePersonaChange) {
            onActivePersonaChange(event.persona);
          }
          
          // Trigger transition animation
          setIsTransitioning(true);
          setTimeout(() => setIsTransitioning(false), 600);
          setPreviousPersona(event.persona);
        } else {
          console.log(`[PersonaIndicator] ⏭️ Event for different conversation, ignoring`);
        }
      };

      // Handle all VoiceLive events to extract persona info
      const handleVoiceLiveEvent = (event: any) => {
        // Filter out noisy event types from logging
        const noisyEventTypes = ['response.audio_transcript.delta', 'response.audio.delta'];
        const shouldLog = !noisyEventTypes.includes(event.type);
        
        // Update persona info from any event that includes it
        if (event.active_persona !== undefined && event.intervention_count !== undefined) {
          if (shouldLog) {
            console.log('[PersonaIndicator] 📥 VoiceLive event with persona info:', {
              active_persona: event.active_persona,
              intervention_count: event.intervention_count,
              event_type: event.type
            });
          }
          
          setPersonaStatus(prev => {
            const currentPersona = prev?.active_persona;
            
            // Only update if persona changed or this is first update
            if (!currentPersona || currentPersona !== event.active_persona) {
              if (shouldLog) {
                console.log(`[PersonaIndicator] 🔄 Updating persona from event: ${currentPersona} → ${event.active_persona}`);
              }
              
              // Trigger transition animation if persona changed
              if (currentPersona && event.active_persona !== currentPersona) {
                setIsTransitioning(true);
                setTimeout(() => setIsTransitioning(false), 600);
              }
              setPreviousPersona(event.active_persona);
              
              return {
                conversation_id: conversationId,
                active_persona: event.active_persona,
                intervention_count: event.intervention_count,
                max_interventions: prev?.max_interventions || 10,
                can_intervene: prev?.can_intervene || true,
                remaining_interventions: (prev?.max_interventions || 10) - event.intervention_count,
                persona_switch_count: prev?.persona_switch_count || 0
              };
            }
            return prev;
          });
        }
      };

      // Subscribe to persona_switched events (highest priority)
      voiceLiveClient.addEventListener('voiceLiveEvent:persona_switched', handlePersonaSwitch);
      
      // Also listen to all VoiceLive events for persona info
      voiceLiveClient.addEventListener('voiceLiveEvent', handleVoiceLiveEvent);

      console.log('[PersonaIndicator] ✅ WebSocket event listeners registered');

      return () => {
        console.log('[PersonaIndicator] 🔌 Cleaning up WebSocket event listeners');
        voiceLiveClient.removeEventListener('voiceLiveEvent:persona_switched', handlePersonaSwitch);
        voiceLiveClient.removeEventListener('voiceLiveEvent', handleVoiceLiveEvent);
      };
    } else {
      console.warn('[PersonaIndicator] ⚠️ voiceLiveClient not available, using HTTP polling fallback');
    }
    // FIXED: Add voiceLiveClient to dependencies to prevent re-initialization on every render
    // Only re-run when conversationId or voiceLiveClient instance actually changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, voiceLiveClient, fetchPersonaStatus]);

  // Persona configuration
  const getPersonaConfig = (persona: string | null) => {
    switch (persona) {
      case 'gentle_teacher':
        return {
          name: 'Gentle Teacher',
          icon: '🎓',
          color: '#4CAF50',
          bgColor: 'bg-green-100',
          borderColor: 'border-green-400',
          textColor: 'text-green-700',
          description: 'Your friendly reading companion'
        };
      case 'pronunciation_coach':
        return {
          name: 'Pronunciation Coach',
          icon: '🎯',
          color: '#FF9800',
          bgColor: 'bg-orange-100',
          borderColor: 'border-orange-400',
          textColor: 'text-orange-700',
          description: 'Helping you master difficult words'
        };
      case 'quiz_critter':
        return {
          name: 'QuizCritter',
          icon: '🎮',
          color: '#2196F3',
          bgColor: 'bg-blue-100',
          borderColor: 'border-blue-400',
          textColor: 'text-blue-700',
          description: 'Fun questions about your story!'
        };
      case 'role_ranger':
        return {
          name: 'RoleRanger',
          icon: '🎭',
          color: '#9C27B0',
          bgColor: 'bg-purple-100',
          borderColor: 'border-purple-400',
          textColor: 'text-purple-700',
          description: 'Story character roleplay'
        };
      default:
        return {
          name: 'Unknown',
          icon: '❓',
          color: '#999',
          bgColor: 'bg-gray-100',
          borderColor: 'border-gray-300',
          textColor: 'text-gray-600',
          description: 'Persona not active'
        };
    }
  };

  if (loading && !personaStatus) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
        <div className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
        <span className="text-xs text-gray-500">Loading persona...</span>
      </div>
    );
  }

  if (error || !personaStatus) {
    return null; // Don't show if error or no data
  }

  const config = getPersonaConfig(personaStatus.active_persona);
  const interventionPercent = (personaStatus.intervention_count / personaStatus.max_interventions) * 100;

  return (
    <div className="relative">
      {/* Stuck State Warning Banner - Shows above persona indicator */}
      {showStuckWarning && personaStatus.active_persona !== 'gentle_teacher' && (
        <div className="absolute -top-12 left-0 right-0 z-50 animate-bounce">
          <div className="bg-red-500 text-white px-4 py-2 rounded-lg shadow-xl border-2 border-red-700 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xl">⚠️</span>
              <div className="flex flex-col">
                <span className="text-xs font-bold">Persona Stuck? ({personaActiveSeconds}s)</span>
                <span className="text-xs">Click Return button or wait for auto-return</span>
              </div>
            </div>
            <button
              onClick={() => setShowStuckWarning(false)}
              className="ml-4 text-white hover:text-red-200 text-sm"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      <div 
        className={`
          flex items-center gap-3 px-4 py-2 rounded-lg border-2 transition-all duration-300
          ${config.bgColor} ${config.borderColor}
          ${isTransitioning ? 'scale-105 shadow-lg' : 'shadow-sm'}
          ${showStuckWarning ? 'border-red-500 ring-2 ring-red-300' : ''}
        `}
        title={`${config.description} - ${personaStatus.intervention_count}/${personaStatus.max_interventions} interventions used`}
      >
      {/* Persona Icon and Name */}
      <div className="flex items-center gap-2">
        <div className="text-2xl transform hover:scale-110 transition-transform">
          {config.icon}
        </div>
        <div className="flex flex-col">
          <span className={`text-sm font-bold ${config.textColor}`}>
            Active AI Persona
          </span>
          <span className={`text-xs font-semibold ${config.textColor}`}>
            {config.name}
          </span>
        </div>
      </div>

      {/* Intervention Counter - Compact */}
      <div className="flex items-center gap-2 ml-auto">
        {/* Readiness Badges */}
        {storyId && (
          <div className="flex items-center gap-1 mr-2">
            <PersonaReadinessBadgeCompact
              personaType="quiz_critter"
              readiness={quizReadiness}
              isAnalyzing={isAnalyzing}
            />
            <PersonaReadinessBadgeCompact
              personaType="role_ranger"
              readiness={roleReadiness}
              isAnalyzing={isAnalyzing}
            />
          </div>
        )}
        
        {/* Quiz Dialog Toggle Button */}
        {onQuizDialogEnabledChange && (
          <button
            onClick={() => onQuizDialogEnabledChange(!quizDialogEnabled)}
            className={`
              flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-all
              ${quizDialogEnabled 
                ? 'bg-green-100 text-green-700 border border-green-300 hover:bg-green-200' 
                : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200'
              }
            `}
            title={`Quiz dialogs are ${quizDialogEnabled ? 'enabled' : 'disabled'}. Click to ${quizDialogEnabled ? 'disable' : 'enable'}.`}
          >
            {quizDialogEnabled ? (
              <>
                <span>👁️</span>
                <span>Quiz Pop Up On</span>
              </>
            ) : (
              <>
                <span>🚫</span>
                <span>Quiz Pop Up Off</span>
              </>
            )}
          </button>
        )}
        
        {/* Role Ranger Toggle Button */}
        {onRoleRangerEnabledChange && (
          <button
            onClick={() => onRoleRangerEnabledChange(!roleRangerEnabled)}
            className={`
              flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-all
              ${roleRangerEnabled 
                ? 'bg-purple-100 text-purple-700 border border-purple-300 hover:bg-purple-200' 
                : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200'
              }
            `}
            title={`Role Ranger is ${roleRangerEnabled ? 'enabled' : 'disabled'}. Click to ${roleRangerEnabled ? 'disable' : 'enable'}.`}
          >
            {roleRangerEnabled ? (
              <>
                <span>🎭</span>
                <span>Roleplay On</span>
              </>
            ) : (
              <>
                <span>🚫</span>
                <span>Roleplay Off</span>
              </>
            )}
          </button>
        )}
        
        {/* Pronunciation Feedback Toggle Button */}
        {onPronunciationEnabledChange && (
          <button
            onClick={() => onPronunciationEnabledChange(!pronunciationEnabled)}
            className={`
              flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-all
              ${pronunciationEnabled 
                ? 'bg-green-100 text-green-700 border border-green-300 hover:bg-green-200' 
                : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200'
              }
            `}
            title={`Pronunciation feedback is ${pronunciationEnabled ? 'enabled' : 'disabled'}. Click to ${pronunciationEnabled ? 'disable' : 'enable'}.`}
          >
            {pronunciationEnabled ? (
              <>
                <span>📊</span>
                {pronunciationActive ? (
                  <span className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></span>
                    <span>Recording</span>
                  </span>
                ) : (
                  <span>Pronunciation On</span>
                )}
              </>
            ) : (
              <>
                <span>🚫</span>
                <span>Pronunciation Off</span>
              </>
            )}
          </button>
        )}
        
        <div className="flex flex-col items-end">
          <span className="text-xs text-gray-600 font-medium">
            Interventions
          </span>
          <div className="flex items-center gap-1">
            <span className={`text-sm font-bold ${config.textColor}`}>
              {personaStatus.intervention_count} / {personaStatus.max_interventions}
            </span>
            {personaStatus.remaining_interventions <= 1 && (
              <span className="text-xs text-red-600">⚠️</span>
            )}
          </div>
        </div>

        {/* Mini Progress Bar */}
        <div className="w-16 h-2 bg-white rounded-full border border-gray-200 overflow-hidden">
          <div 
            className="h-full transition-all duration-500"
            style={{ 
              width: `${interventionPercent}%`,
              backgroundColor: interventionPercent > 80 ? '#f44336' : config.color
            }}
          />
        </div>

        {/* Emergency Return Button with Duration - Only show when NOT on Gentle Teacher */}
        {personaStatus.active_persona !== 'gentle_teacher' && (
          <div className="flex items-center gap-2">
            {/* Active Duration Indicator */}
            <div className={`
              px-2 py-1 rounded text-xs font-mono
              ${personaActiveSeconds >= 60 ? 'bg-red-100 text-red-700 font-bold' : 'bg-gray-100 text-gray-600'}
            `}>
              {personaActiveSeconds}s
            </div>
            
            <button
              onClick={handleForceReturn}
              disabled={isForceReturning}
              className={`
                px-3 py-1.5 rounded-lg text-xs font-semibold
                transition-all duration-200 border-2
                ${isForceReturning 
                  ? 'bg-gray-200 text-gray-400 border-gray-300 cursor-not-allowed' 
                  : `${showStuckWarning ? 'bg-red-100 border-red-500 text-red-800 animate-pulse' : 'bg-red-50 text-red-700 border-red-300'} hover:bg-red-100 hover:border-red-400 hover:shadow-md active:scale-95`
                }
              `}
              title={`Force return to Gentle Teacher (active for ${personaActiveSeconds}s)`}
            >
              {isForceReturning ? (
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                  <span>Returning...</span>
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <span>🔙</span>
                  <span>Return</span>
                </div>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Transition Animation */}
      {isTransitioning && (
        <div className="absolute top-0 left-0 right-0 bottom-0 flex items-center justify-center bg-white/80 rounded-lg">
          <div className="flex items-center gap-2 animate-bounce">
            <span className="text-2xl">✨</span>
            <span className="text-xs font-bold text-purple-600">Switching...</span>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default PersonaIndicatorCompact;
